import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CreditCard, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  getLoanOffers, createLoanOffer, updateLoanOffer,
  deleteLoanOffer, loadSampleOffers,
} from "@/lib/api";
import AIOfferInput from "@/components/AIOfferInput";

const EMPTY_FORM = {
  lender_name: "", product_type: "Term Loan", loan_amount: "",
  net_funding_amount: "", term_length: "12", payment_frequency: "monthly",
  interest_rate: "", factor_rate: "", total_payback: "",
  origination_fees: "0", closing_costs: "0", prepayment_penalty: "",
  personal_guarantee: false, collateral_required: false, notes: "",
};

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

export default function LoanOffers({ onDataChange, analysisId, refreshKey, requireAuth }) {
  const [offers, setOffers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchData = useCallback(async () => {
    try {
      const res = await getLoanOffers(analysisId);
      setOffers(res.data.offers || []);
    } catch (e) {
      console.error(e);
    }
  }, [analysisId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleSubmit = async () => {
    if (requireAuth && !requireAuth()) return;
    if (!form.lender_name || !form.loan_amount) {
      toast.error("Lender name and loan amount are required");
      return;
    }
    const payload = {
      ...form,
      loan_amount: parseFloat(form.loan_amount) || 0,
      net_funding_amount: form.net_funding_amount ? parseFloat(form.net_funding_amount) : null,
      term_length: parseInt(form.term_length) || 12,
      interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
      factor_rate: form.factor_rate ? parseFloat(form.factor_rate) : null,
      total_payback: form.total_payback ? parseFloat(form.total_payback) : null,
      origination_fees: parseFloat(form.origination_fees) || 0,
      closing_costs: parseFloat(form.closing_costs) || 0,
    };
    try {
      if (editingId) {
        await updateLoanOffer(editingId, payload);
        toast.success("Offer updated");
      } else {
        await createLoanOffer(payload, analysisId);
        toast.success("Offer added");
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
      fetchData();
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to save offer");
    }
  };

  const handleEdit = (offer) => {
    setEditingId(offer.id);
    setForm({
      lender_name: offer.lender_name || "",
      product_type: offer.product_type || "Term Loan",
      loan_amount: String(offer.loan_amount || ""),
      net_funding_amount: offer.net_funding_amount ? String(offer.net_funding_amount) : "",
      term_length: String(offer.term_length || "12"),
      payment_frequency: offer.payment_frequency || "monthly",
      interest_rate: offer.interest_rate ? String(offer.interest_rate) : "",
      factor_rate: offer.factor_rate ? String(offer.factor_rate) : "",
      total_payback: offer.total_payback ? String(offer.total_payback) : "",
      origination_fees: String(offer.origination_fees || "0"),
      closing_costs: String(offer.closing_costs || "0"),
      prepayment_penalty: offer.prepayment_penalty || "",
      personal_guarantee: offer.personal_guarantee || false,
      collateral_required: offer.collateral_required || false,
      notes: offer.notes || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteLoanOffer(id);
      toast.success("Offer deleted");
      fetchData();
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleLoadSample = async () => {
    try {
      await loadSampleOffers(analysisId);
      toast.success("Sample loan offers loaded");
      fetchData();
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to load sample offers");
    }
  };

  const set = (key) => (e) => {
    const val = e?.target !== undefined ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loan-offers-section">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <AIOfferInput analysisId={analysisId} onOffersCreated={() => { fetchData(); onDataChange?.(); }} requireAuth={requireAuth} />

        <Dialog open={showForm} onOpenChange={(open) => {
          if (open && requireAuth && !requireAuth()) return;
          setShowForm(open);
          if (!open) { setEditingId(null); setForm({ ...EMPTY_FORM }); }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-offer-btn" variant="outline" className="border-[#E7E5DF] hover:bg-[#F1EFEA]">
              <Plus className="w-4 h-4 mr-2" />
              Add Manually
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">
                {editingId ? "Edit Loan Offer" : "Add Loan Offer"}
              </DialogTitle>
              <DialogDescription>Fill in the loan details below.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <Label>Lender Name *</Label>
                <Input data-testid="offer-lender-input" value={form.lender_name} onChange={set("lender_name")} placeholder="e.g. BlueVine Capital" />
              </div>
              <div>
                <Label>Product Type</Label>
                <Select value={form.product_type} onValueChange={set("product_type")}>
                  <SelectTrigger data-testid="offer-product-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Term Loan", "Short Term Loan", "SBA 7(a) Loan", "Line of Credit", "Merchant Cash Advance", "Equipment Financing", "Invoice Factoring", "Other"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Loan Amount *</Label>
                <Input data-testid="offer-amount-input" type="number" value={form.loan_amount} onChange={set("loan_amount")} placeholder="150000" />
              </div>
              <div>
                <Label>Net Funding Amount</Label>
                <Input data-testid="offer-net-funding-input" type="number" value={form.net_funding_amount} onChange={set("net_funding_amount")} placeholder="Auto if blank" />
              </div>
              <div>
                <Label>Term Length (months)</Label>
                <Input data-testid="offer-term-input" type="number" value={form.term_length} onChange={set("term_length")} placeholder="12" />
              </div>
              <div>
                <Label>Payment Frequency</Label>
                <Select value={form.payment_frequency} onValueChange={set("payment_frequency")}>
                  <SelectTrigger data-testid="offer-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["daily", "weekly", "biweekly", "monthly"].map((f) => (
                      <SelectItem key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Interest Rate (%)</Label>
                <Input data-testid="offer-rate-input" type="number" step="0.01" value={form.interest_rate} onChange={set("interest_rate")} placeholder="10.5" />
              </div>
              <div>
                <Label>Factor Rate</Label>
                <Input data-testid="offer-factor-input" type="number" step="0.01" value={form.factor_rate} onChange={set("factor_rate")} placeholder="1.35" />
              </div>
              <div>
                <Label>Total Payback (optional)</Label>
                <Input type="number" value={form.total_payback} onChange={set("total_payback")} placeholder="Auto-calculated" />
              </div>
              <div>
                <Label>Origination Fees</Label>
                <Input type="number" value={form.origination_fees} onChange={set("origination_fees")} placeholder="0" />
              </div>
              <div>
                <Label>Closing Costs / Broker Fees</Label>
                <Input type="number" value={form.closing_costs} onChange={set("closing_costs")} placeholder="0" />
              </div>
              <div>
                <Label>Prepayment Penalty</Label>
                <Input value={form.prepayment_penalty} onChange={set("prepayment_penalty")} placeholder="None" />
              </div>
              <div className="flex items-center gap-3 pt-5">
                <Switch
                  data-testid="offer-pg-switch"
                  checked={form.personal_guarantee}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, personal_guarantee: v }))}
                />
                <Label>Personal Guarantee</Label>
              </div>
              <div className="flex items-center gap-3 pt-5">
                <Switch
                  data-testid="offer-collateral-switch"
                  checked={form.collateral_required}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, collateral_required: v }))}
                />
                <Label>Collateral Required</Label>
              </div>
              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={set("notes")} placeholder="Additional notes..." />
              </div>
              <div className="sm:col-span-2">
                <Button data-testid="submit-offer-btn" onClick={handleSubmit} className="w-full bg-[#1A362D] hover:bg-[#264B40]">
                  {editingId ? "Update Offer" : "Add Offer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Button
          data-testid="load-sample-offers-btn"
          variant="outline"
          onClick={handleLoadSample}
          className="border-[#E7E5DF] hover:bg-[#F1EFEA]"
        >
          <Database className="w-4 h-4 mr-2" />
          Sample Offers
        </Button>
      </div>

      {/* Offer Cards */}
      {offers.length === 0 ? (
        <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center">
          <CreditCard className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
          <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
            No Loan Offers
          </h3>
          <p className="text-[#57534E] text-sm">
            Add loan offers manually or load sample data to begin comparison.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {offers.map((offer) => (
            <div
              key={offer.id}
              data-testid={`offer-card-${offer.id}`}
              className="bg-white border border-[#E7E5DF] rounded-md p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
                    {offer.lender_name}
                  </h4>
                  <span className="inline-block mt-1 text-xs tracking-[0.1em] uppercase font-bold px-2 py-0.5 rounded bg-[#F1EFEA] text-[#57534E]">
                    {offer.product_type}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    data-testid={`edit-offer-${offer.id}`}
                    onClick={() => handleEdit(offer)}
                    className="p-1.5 text-[#78716C] hover:text-[#1A362D] transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-offer-${offer.id}`}
                    onClick={() => handleDelete(offer.id)}
                    className="p-1.5 text-[#78716C] hover:text-[#E07A5F] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <p className="text-[#78716C] text-xs">Loan Amount</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917]">{fmtMoney(offer.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs">Net Funding</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917]">{fmtMoney(offer.net_funding_amount)}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs">Total Payback</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] text-[#E07A5F]">{fmtMoney(offer.total_payback)}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs">Monthly Payment</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917]">{fmtMoney(offer.monthly_payment)}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs">Term</p>
                  <p className="font-medium text-[#1C1917]">{offer.term_length} mo / {offer.payment_frequency}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs">Financing Cost</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] text-[#E07A5F]">{fmtMoney(offer.total_financing_cost)}</p>
                </div>
                {offer.estimated_apr && (
                  <div>
                    <p className="text-[#78716C] text-xs">Est. APR</p>
                    <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917]">{offer.estimated_apr}%</p>
                  </div>
                )}
                {(offer.factor_rate) && (
                  <div>
                    <p className="text-[#78716C] text-xs">Factor Rate</p>
                    <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917]">{offer.factor_rate}x</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-4 pt-3 border-t border-[#E7E5DF]">
                {offer.personal_guarantee && (
                  <span className="text-xs px-2 py-0.5 rounded bg-[#E3B505]/15 text-[#E3B505] font-medium">PG Required</span>
                )}
                {offer.collateral_required && (
                  <span className="text-xs px-2 py-0.5 rounded bg-[#E07A5F]/15 text-[#E07A5F] font-medium">Collateral</span>
                )}
                {offer.prepayment_penalty && offer.prepayment_penalty.toLowerCase() !== "none" && (
                  <span className="text-xs px-2 py-0.5 rounded bg-[#78716C]/15 text-[#78716C] font-medium">Prepay Penalty</span>
                )}
              </div>

              {offer.notes && (
                <p className="text-xs text-[#78716C] mt-2 italic">{offer.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
